import React, { useState } from 'react';
import { Alert, Button, Card, Descriptions, Input, Space, Tag, Typography, message } from 'antd';
import { SafetyOutlined, UndoOutlined } from '@ant-design/icons';
import firebase from 'firebase/app';

const { Text, Paragraph } = Typography;

const PREVIEW_CALLABLE = 'userService-restoreHandler-adminPreviewRestoreCredential';
const RESTORE_CALLABLE = 'userService-restoreHandler-adminRestoreCredential';

/**
 * Why this preview cannot be restored, or null when it can. One place decides;
 * the UI only displays.
 */
export function restoreBlockReason(preview) {
  if (!preview) return null;
  if (!preview.tombstoned) {
    return 'No sign-in deletion is recorded for this person — there is nothing to restore.';
  }
  if (!preview.email) {
    return 'No email was recorded when the sign-in was deleted; it cannot be recreated from here.';
  }
  if (preview.existingAuthForUid || preview.existingAuthForEmail) {
    return 'A credential already exists for this uid or its email (details above). Resolve it first.';
  }
  return null;
}

/** Post-restore confirmation, loud when the recovery email did not go out. */
function RestoredCard({ restored }) {
  return (
    <Card style={{ marginBottom: 12, borderColor: '#52c41a' }} data-testid="restore-credential-section">
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Text strong><SafetyOutlined /> Credential restored</Text>
        <Text>{restored.email} — same uid, nothing re-plumbed.</Text>
        {restored.recoveryEmailSent === false ? (
          <Alert
            type="warning"
            showIcon
            message="Recovery email NOT sent"
            description={restored.nextStep}
            style={{ borderRadius: 8 }}
            data-testid="restore-email-failed"
          />
        ) : (
          <Text type="secondary">{restored.nextStep}</Text>
        )}
      </Space>
    </Card>
  );
}

/** The informed-choice table: every credential and doc that already exists. */
function RestorePreviewDetails({ preview }) {
  return (
    <Descriptions bordered column={1} size="small">
      <Descriptions.Item label="Credential to restore">
        <Text code>{preview.email || '— no email was recorded at deletion —'}</Text>
        {' '}
        {preview.emailVerifiedAtDeletion
          ? <Tag color="green">was verified</Tag>
          : <Tag color="orange">was NOT verified</Tag>}
      </Descriptions.Item>
      <Descriptions.Item label="Auth user for this uid">
        {preview.existingAuthForUid
          ? <Text type="danger">EXISTS — nothing to restore</Text>
          : <Text type="secondary">none (deleted) — restorable</Text>}
      </Descriptions.Item>
      <Descriptions.Item label="Auth user holding this email">
        {preview.existingAuthForEmail ? (
          <Space direction="vertical" size={0}>
            <Text type="danger" data-testid="email-holder-warning">
              HELD by uid <Text code>{preview.existingAuthForEmail.uid}</Text>
              {' '}({(preview.existingAuthForEmail.providers || []).join(', ') || 'no providers'})
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              created {preview.existingAuthForEmail.createdAt || '?'} · last sign-in{' '}
              {preview.existingAuthForEmail.lastSignInAt || 'never'} — likely a lockout-era
              duplicate. Resolve it first; restore refuses to create a second credential
              for one email.
            </Text>
          </Space>
        ) : <Text type="secondary">none — email is free</Text>}
      </Descriptions.Item>
      <Descriptions.Item label="Other users docs with this email">
        {preview.otherUserDocs && preview.otherUserDocs.length
          ? preview.otherUserDocs.map((u) => (
            <div key={u.uid}>
              <Text code>{u.uid}</Text>
              {u.deleted && <Tag color="red">sign-in deleted</Tag>}
              {u.clientId && <Tag>client {u.clientId}</Tag>}
            </div>
          ))
          : <Text type="secondary">none</Text>}
      </Descriptions.Item>
      <Descriptions.Item label="Clients owned by this uid">
        {preview.ownedClients && preview.ownedClients.length
          ? preview.ownedClients.map((c) => (
            <div key={c.clientId}>
              <Text code>{c.clientId}</Text> {c.displayName || ''}{' '}
              <Tag color={c.status === 'live' ? 'green' : 'orange'}>{c.status}</Tag>
            </div>
          ))
          : <Text type="secondary">none — check this is the right uid before restoring</Text>}
      </Descriptions.Item>
    </Descriptions>
  );
}

/**
 * Super-admin restore of a deleted auth credential — SAME uid, so ownership
 * (clients.ownerId), team members-map keys and the users doc all keep working
 * with zero re-plumbing.
 *
 * Deliberately two-step: the preview shows every credential that already exists
 * for this uid or its email (and any other users docs sharing the email), so
 * the restore is an informed choice; then the server re-checks everything and
 * requires the email retyped — the confirmation is verified server-side, this
 * UI cannot skip it. Shown only for tombstoned users docs.
 */
export function RestoreCredentialSection({ uid }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [restored, setRestored] = useState(null);
  const [error, setError] = useState(null);

  const loadPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const result = await firebase.functions().httpsCallable(PREVIEW_CALLABLE)({ uid });
      setPreview(result.data);
    } catch (err) {
      setError(err.message || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const doRestore = async () => {
    setRestoring(true);
    setError(null);
    try {
      const result = await firebase.functions().httpsCallable(RESTORE_CALLABLE)({ uid, confirmEmail });
      setRestored(result.data);
      message.success('Credential restored');
    } catch (err) {
      setError(err.message || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  if (restored) {
    return <RestoredCard restored={restored} />;
  }

  const blockReason = restoreBlockReason(preview);

  return (
    <Card
      style={{ marginBottom: 12, borderColor: '#faad14' }}
      data-testid="restore-credential-section"
      title={<Text strong><UndoOutlined /> Restore credential (super admin)</Text>}
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="This recreates the deleted sign-in with the SAME uid — and EMAILS the person."
          description={'Ownership, team membership and login all resolve again without touching any data. '
            + 'Restoring sends a recovery email to the address below telling the person their access '
            + 'is back and to use "Forgot password" to sign in — do not restore if that address '
            + 'should not be contacted. Preview first — the restore refuses to run over any '
            + 'existing credential.'}
          style={{ borderRadius: 8 }}
        />

        {!preview && (
          <Button loading={loadingPreview} onClick={loadPreview} data-testid="restore-preview-button">
            Preview restore…
          </Button>
        )}

        {preview && (
          <>
            <RestorePreviewDetails preview={preview} />

            {blockReason ? (
              <Alert
                type="error"
                showIcon
                message="Restore is blocked"
                description={blockReason}
                style={{ borderRadius: 8 }}
              />
            ) : (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Paragraph type="secondary" style={{ margin: 0 }} data-testid="restore-email-reminder">
                  Restoring will <Text strong>send a recovery email to {preview.email}</Text>.
                  Type the email exactly to confirm — the server checks this, not just this form.
                </Paragraph>
                <Input.Group compact style={{ display: 'flex', width: '100%', maxWidth: 460 }}>
                  <Input
                    placeholder={preview.email}
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    data-testid="restore-confirm-email"
                    style={{ flex: 1 }}
                  />
                  <Button
                    danger
                    type="primary"
                    loading={restoring}
                    disabled={confirmEmail.trim().toLowerCase() !== preview.email}
                    onClick={doRestore}
                    data-testid="restore-confirm-button"
                  >
                    Restore credential
                  </Button>
                </Input.Group>
              </Space>
            )}
          </>
        )}

        {error && <Alert type="error" showIcon message={error} style={{ borderRadius: 8 }} />}
      </Space>
    </Card>
  );
}

export default RestoreCredentialSection;
